import { FadeUp, SectionHeading } from "./Reveal";

const W = "/projects/goodwill";

const PhaseLabel = ({ num, title }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-display text-4xl md:text-5xl font-bold text-[#4361C2]">{num}</span>
    <span className="text-xs tracking-[0.3em] uppercase text-ash border-b border-white/15 pb-1">{title}</span>
  </div>
);

const Caption = ({ children }) => (
  <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-ash">{children}</div>
);

const FACTS = [
  ["Client", "Good Will Baptist Church"],
  ["Location", "Adams Morgan, Washington DC"],
  ["Scope", "Restroom Renovation"],
  ["Role", "Project Manager"],
  ["Architect of Record", "Paul Wilson"],
  ["Context", "Historic Landmark — Built 1900"],
];

export const CaseStudyGoodwill = () => (
  <section id="case-study-goodwill" className="px-6 md:px-12 py-28 md:py-40" data-testid="goodwill-case-study-section">
    <SectionHeading index="Case Study 04 — Historic Renovation" title="Good Will Baptist Church" testId="goodwill-case-study-heading" />

    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-5">
        <dl className="border border-white/15 divide-y divide-white/10" data-testid="goodwill-case-study-facts">
          {FACTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-4 px-5 py-4">
              <dt className="text-[10px] tracking-[0.3em] uppercase text-ash shrink-0">{k}</dt>
              <dd className="text-sm font-medium text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-6 md:col-start-7">
        <p className="text-lg md:text-xl font-light leading-relaxed text-paper" data-testid="goodwill-case-study-intro">
          As Project Manager, Lonny carried this renovation end to end — surveying the existing
          conditions of the 1900 stone church, developing the bathroom design layouts around the
          board's program needs, and producing the drawings with Paul Wilson, Architect of Record.
        </p>
        <p className="mt-5 text-sm leading-relaxed text-ash">
          He selected finishes for the board's approval, served as liaison between the stakeholders
          and the general contractor, monitored all construction with regular progress updates, and
          reviewed every change order and pay application along the way.
        </p>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="01" title="The Landmark" />
      </FadeUp>
      <FadeUp className="md:col-span-7">
        <img
          src={`${W}/church.jpg`}
          alt="Good Will Baptist Church — historic stone church in Adams Morgan"
          loading="lazy"
          className="w-full aspect-[4/3] object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700"
          data-testid="goodwill-church-image"
        />
        <Caption>Kalorama Rd NW — a neighborhood fixture since 1900</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-5 flex items-center">
        <blockquote className="border-l-2 border-[#4361C2] pl-6 md:pl-8 py-2" data-testid="goodwill-case-study-quote">
          <p className="font-display text-2xl md:text-3xl uppercase font-bold leading-tight tracking-tight">
            "A 1900 landmark. New restrooms. The history stays untouched."
          </p>
        </blockquote>
      </FadeUp>
      <FadeUp className="md:col-span-12 mt-10">
        <PhaseLabel num="02" title="The Drawings" />
      </FadeUp>
      <FadeUp className="md:col-span-8">
        <div className="border border-white/15 overflow-hidden bg-[#0d1530]">
          <img
            src={`${W}/drawings.jpg`}
            alt="Restroom renovation drawing set — demolition, architectural and plumbing plans"
            loading="lazy"
            className="w-full invert opacity-80 mix-blend-screen"
            data-testid="goodwill-drawings-image"
          />
        </div>
        <Caption>Demolition, architectural + plumbing plans — full permit set</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-4 flex items-end">
        <p className="text-sm leading-relaxed text-ash" data-testid="goodwill-case-study-outro">
          Surveys, layouts, and finish selections shaped with the board — then drawn with Paul
          Wilson, priced with the GC, and monitored through every change order and pay application
          so the church never missed a Sunday.
        </p>
      </FadeUp>
    </div>
  </section>
);
